/**
 * What a job is worth.
 *
 * Two rules, both meant to be explainable to a six-year-old:
 *
 *   1. Bigger jobs are worth more. One point per five minutes of effort, so
 *      wiping the table is 1 and mowing the lawn is 9.
 *   2. A job left undone is worth more tomorrow. One extra point per day late.
 *
 * The second rule is the whole point of rolling work over: nothing duplicates
 * when a day passes — there is still exactly one "bins out" — it simply grows
 * a bounty until somebody takes it. That falls straight out of the derived-due
 * model in `schedule.ts`, where a task is a standing obligation rather than a
 * queue of dated instances.
 *
 * The bounty is capped at the base, so a job can at most double. Without the
 * cap, a seasonal job nobody touched for a year would be worth more than every
 * other job in the house combined, and the winning move would be to ignore
 * things on purpose.
 */

/** Minutes of effort that buy one point. */
export const MINUTES_PER_POINT = 5;

export function basePoints(effortMin: number): number {
  return Math.max(1, Math.round(effortMin / MINUTES_PER_POINT));
}

/**
 * The bounty a job has accrued by being late.
 *
 * Never-done tasks get nothing: `daysLate` is 0 for them because nobody knows
 * how long they have really been waiting, and inventing a number would inflate
 * the first week the app is used.
 */
export function latePoints(effortMin: number, daysLate: number): number {
  if (daysLate <= 0) return 0;
  return Math.min(daysLate, basePoints(effortMin));
}

/** What completing this job right now is worth. */
export function pointsFor(effortMin: number, daysLate: number): number {
  return basePoints(effortMin) + latePoints(effortMin, daysLate);
}

/** "9 + 3" — shown next to a job so the bounty is visible, not just felt. */
export function describePoints(effortMin: number, daysLate: number): string {
  const bonus = latePoints(effortMin, daysLate);
  return bonus > 0 ? `${basePoints(effortMin)} + ${bonus}` : String(basePoints(effortMin));
}
